import { ClientEmployeeFragment } from "@graphql/client-employees/clientEmployees.g";
import React from "react";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";

import {
  CLIENT_EMPLOYEE_TABLE_COLUMN_LABELS,
  ClientEmployeeTableColumns,
  DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_SETTINGS,
} from "./UI/ClientEmployeesTableColumns";

/**
 * Default filter values for Client Employees
 */
export const DEFAULT_CLIENT_EMPLOYEE_FILTER_VALUES = {
  nameSearch: "",
  emailSearch: "",
};

/**
 * Comprehensive table configuration for Client Employees
 */
export const ClientEmployeesTableConfig = createTableConfiguration<
  ClientEmployeeFragment,
  ClientEmployeeTableColumns
>({
  tableId: "client-employees",
  columns: {
    [ClientEmployeeTableColumns.name]: {
      id: ClientEmployeeTableColumns.name,
      label:
        CLIENT_EMPLOYEE_TABLE_COLUMN_LABELS[ClientEmployeeTableColumns.name],
      fieldPath: "name",
      sortField: "name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: ClientEmployeeFragment) => {
        return renderName(value, row, () => {});
      },
    },
    [ClientEmployeeTableColumns.email]: {
      id: ClientEmployeeTableColumns.email,
      label:
        CLIENT_EMPLOYEE_TABLE_COLUMN_LABELS[ClientEmployeeTableColumns.email],
      fieldPath: "email",
      sortField: "email",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderEmail(value);
      },
    },
    [ClientEmployeeTableColumns.appsUsed]: {
      id: ClientEmployeeTableColumns.appsUsed,
      label:
        CLIENT_EMPLOYEE_TABLE_COLUMN_LABELS[
          ClientEmployeeTableColumns.appsUsed
        ],
      fieldPath: "resourceGroupCount",
      sortField: "resource_group_count",
      type: "count",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderAppsUsed(value);
      },
    },
    [ClientEmployeeTableColumns.lastActivity]: {
      id: ClientEmployeeTableColumns.lastActivity,
      label:
        CLIENT_EMPLOYEE_TABLE_COLUMN_LABELS[
          ClientEmployeeTableColumns.lastActivity
        ],
      fieldPath: "sessions.aggregate.max.updatedAt",
      sortField: "sessions_max_updated_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderLastActivity(value);
      },
    },
  },
  defaultColumnOrder: DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_SETTINGS,
});

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues = () => ({
  ...DEFAULT_CLIENT_EMPLOYEE_FILTER_VALUES,
});

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (filterValues: {
  nameSearch: string;
  emailSearch: string;
}): boolean => {
  return Boolean(
    (filterValues.nameSearch && filterValues.nameSearch.trim()) ||
      (filterValues.emailSearch && filterValues.emailSearch.trim()),
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderName(
  value: unknown,
  row: ClientEmployeeFragment,
  onDetailsClick: (employeeId: string) => void,
): React.ReactNode {
  return (
    <Clickable onClick={() => onDetailsClick(row.id)} outline="sm" stretch>
      <Table.UserCell name={String(value)} />
    </Clickable>
  );
}

export function renderEmail(value: unknown): React.ReactNode {
  return <Text truncate>{String(value)}</Text>;
}

export function renderAppsUsed(value: unknown): React.ReactNode {
  return <Text>{String(value || 0)}</Text>;
}

export function renderLastActivity(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} dayOnly />;
}
